// @ts-check
// Etapa 4 (doc 14, 9.2 "importData"): progresso por lote e relatório final com criados, atualizados, ignorados,
// erros e a planilha de erros para corrigir e importar de novo.
import { html } from 'htm/preact';
import { Icon } from '../../ui/icon.js';
import { Button } from '../../ui/button.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ done: number, total: number, isStopping: boolean, onStop: () => void }} props
 */
export function ImportProgress({ done, total, isStopping, onStop }) {
  const percent = total > 0 ? Math.round((100 * done) / total) : 0;
  return html`<div class="spreadsheet-import-progress" aria-live="polite">
    <${Icon} name="IconFileImport" size=${32} />
    <h3 class="spreadsheet-import-step-title">${t('spreadsheetImport.progress.title')}</h3>
    <div class="spreadsheet-import-progress-bar" role="progressbar" aria-valuemin="0" aria-valuemax=${total} aria-valuenow=${done}>
      <span style=${{ width: `${percent}%` }}></span>
    </div>
    <div class="spreadsheet-import-hint">${isStopping ? t('spreadsheetImport.progress.cancelling') : t('spreadsheetImport.progress.count', { done, total })}</div>
    <${Button} title=${t('spreadsheetImport.progress.cancel')} size="small" variant="secondary" disabled=${isStopping} onClick=${onStop} />
  </div>`;
}

/**
 * @param {{ summary: { created: number, updated: number, skipped: number, failed: number }, notSent: number, cancelled: boolean,
 *   counters: Record<string, number> | null, createdRelations: Array<{ label: string }>, hasErrors: boolean, onDownloadErrors: () => void }} props
 */
export function ImportReport({ summary, notSent, cancelled, counters, createdRelations, hasErrors, onDownloadErrors }) {
  const cards = [
    ['created', summary.created, 'success'], ['updated', summary.updated, 'info'], ['skipped', summary.skipped, 'neutral'], ['failed', summary.failed, 'error'],
  ];
  return html`<div class="spreadsheet-import-report">
    <h3 class="spreadsheet-import-step-title">
      <${Icon} name=${cancelled ? 'IconAlertTriangle' : 'IconCircleCheck'} size=${20} />
      ${cancelled ? t('spreadsheetImport.report.cancelledTitle') : t('spreadsheetImport.report.title')}
    </h3>
    <div class="spreadsheet-import-report-cards">
      ${cards.map(([key, value, variant]) => html`<div class="spreadsheet-import-report-card" data-variant=${variant} key=${key}>
        <span class="spreadsheet-import-report-value">${Number(value).toLocaleString('pt-BR')}</span>
        <span class="spreadsheet-import-report-label">${t(`spreadsheetImport.report.${key}`)}</span>
      </div>`)}
    </div>
    ${notSent > 0 ? html`<p class="spreadsheet-import-hint">${t('spreadsheetImport.report.notSent', { count: notSent })}</p>` : null}
    ${counters && Object.keys(counters).length > 0
      ? html`<p class="spreadsheet-import-hint">${t('spreadsheetImport.report.familyCounters', {
          households: counters.households ?? 0, people: counters.people ?? 0, householdMembers: counters.householdMembers ?? 0,
          enrollmentOpportunities: counters.enrollmentOpportunities ?? 0, opportunityStudents: counters.opportunityStudents ?? 0,
        })}</p>`
      : null}
    ${createdRelations.length > 0
      ? html`<p class="spreadsheet-import-hint">${t('spreadsheetImport.report.createdRelations', {
          count: createdRelations.length, labels: createdRelations.slice(0, 8).map((relation) => relation.label).join(', ') + (createdRelations.length > 8 ? '...' : ''),
        })}</p>`
      : null}
    ${hasErrors
      ? html`<div><${Button} title=${t('spreadsheetImport.report.downloadErrors')} icon="IconDownload" size="small" variant="secondary" onClick=${onDownloadErrors} /></div>`
      : null}
  </div>`;
}
